import styled from "styled-components";
import {
  formatDate,
  formatDate2,
  formatDistanceFromNow,
  isDateInFuture,
  toPascalCase,
} from "../../utils/helpers";
import { Link } from "react-router-dom";
import LeadsButton from "../../ui/LeadsButton";
import { Badge } from "../../components/ui/badge";
import { Separator } from "../../components/ui/separator";
import ThemedSkeleton from "../../ui/ThemedSkeleton";
import { useContext } from "react";
import { SearchContext } from "../../pages/Search";
import { Dot, Landmark } from "lucide-react";
import { AiOutlineFileProtect } from "react-icons/ai";
import { LiaFileContractSolid } from "react-icons/lia";
import { IoLocationOutline } from "react-icons/io5";
import { differenceInDays } from "date-fns";
import { searchTypes } from "./searchTypes";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { naicsCodes, setAsideCode } from "./FilterValues";

const Card = styled.div`
  border: 1px solid var(--gray-300);
  margin-bottom: 2.5rem;
  background-color: var(--gray-50);
  transition: all 0.3s;
  border-radius: 0.5rem;
  box-shadow: var(--shadow-sm);
  padding: 1.5rem;
  overflow: hidden;

  &:hover {
    outline: none;
    box-shadow: var(--shadow-md);
    transform: translateY(-2px);
    border: 1px solid var(--gray-300);
  }
`;

export function NotAvailable() {
  return <span className="text-muted-foreground text-xs">-</span>;
}

function DateBadges({ postedDate, dueDate }) {
  if (differenceInDays(new Date(), new Date(postedDate)) < 7) {
    return (
      <Badge className="mb-2 bg-primary/80">
        <span className="">New</span>
      </Badge>
    );
  }
  return null;
}

function BaseCard({
  id,
  title,
  summary,
  tags,
  tagColCount = tags.length,
  postedDate,
  updatedDate,
  dueDate,
}) {
  const { searchType } = useContext(SearchContext);
  const tagRowCount = Math.ceil(Number(tags.length) / tagColCount);
  return (
    <Card role="row">
      <DateBadges postedDate={postedDate} dueDate={dueDate} />
      <div className="grid grid-cols-[85%_15%]">
        <div className="grid grid-rows-[1fr_0.1fr] space-y-4">
          <div className="flex flex-col gap-4">
            <Link
              className="cursor-pointer font-semibold hover:underline"
              to={`/contract/${searchType}/${id}`}
            >
              {title}
            </Link>
            <div className="text-accent-foreground pr-2 leading-relaxed">
              {summary}
            </div>
          </div>
          <div
            className="gap-x-12 gap-y-2 px-2"
            style={{
              display: "grid",
              gridTemplateColumns: `repeat(${tagColCount}, minmax(0, 1fr))`,
              gridTemplateRows: `repeat(${tagRowCount}, minmax(0, 1fr))`,
            }}
          >
            {tags.map((tag) => (
              <div
                className="flex flex-col gap-1 justify-start items-start"
                key={tag.label}
              >
                {tag?.label && (
                  <span
                    key={tag.label}
                    className="text-muted-foreground font-medium "
                  >
                    {tag.label}{" "}
                  </span>
                )}
                {tag?.value ? (
                  <Badge
                    key={tag.value}
                    variant="secondary"
                    className="text-sm rounded-xl h-full "
                  >
                    <span className="">{tag.value}</span>
                  </Badge>
                ) : (
                  <NotAvailable />
                )}
              </div>
            ))}
          </div>
        </div>
        <div className="grid grid-rows-[3rem_2ps_1fr_2px_1fr] grid-cols-1 gap-3 pl-6 border-l">
          <div className="justify-self-center">
            <LeadsButton proposalId={id} searchType={searchType} />
          </div>
          <Separator className="bg-muted/70" />

          <div className="flex flex-col gap-1">
            <div className="text-muted-foreground row-span-2 justify">
              Posted
            </div>
            <div>
              <div> {formatDate2(postedDate) ?? <NotAvailable />}</div>
              <div className="text-accent-foreground text-sm ">
                {formatDistanceFromNow(postedDate) ?? <NotAvailable />}
              </div>
            </div>
          </div>
          <Separator className="bg-muted/70" />
          <div className="flex flex-col gap-1">
            <div className="text-muted-foreground row-span-2 justify">Due</div>
            <div>
              <div> {formatDate2(dueDate) ?? <NotAvailable />}</div>
              <div className="text-accent-foreground text-sm">
                {isDateInFuture(dueDate) ?? <NotAvailable /> ? (
                  formatDistanceFromNow(dueDate) ?? <NotAvailable />
                ) : (
                  <span className="text-destructive">Past Due</span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
}
function FederalCard({ rowData, ...props }) {
  const { id, metadata } = rowData;
  const {
    Title,
    Summary,
    Type,
    ResponseDeadLine,
    "Department/Ind.Agency": Department_Ind_Agency,
    SetASideCode,
    PostedDate,
    NaicsCode,
  } = metadata;
  const tags = [
    { value: toPascalCase(Type), label: "Type", icon: LiaFileContractSolid },
    {
      value: toPascalCase(Department_Ind_Agency),
      label: "Agency",
      icon: Landmark,
    },
    {
      value: (
        <TooltipProvider delayDuration={100}>
          <Tooltip>
            <TooltipTrigger className="hover:font-bold">
              {parseInt(NaicsCode)}
            </TooltipTrigger>
            <TooltipContent>
              <p>
                {naicsCodes.find((n) => n.code === parseInt(NaicsCode))
                  ?.name ?? (
                  <span className="text-muted-foreground">
                    No description available{" "}
                  </span>
                )}
              </p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ),
      label: "NAICS",
      icon: AiOutlineFileProtect,
    },
    {
      value: (
        <TooltipProvider delayDuration={100} >
          <Tooltip>
            <TooltipTrigger className="hover:font-bold">
              {SetASideCode || "-"}
            </TooltipTrigger>
            <TooltipContent side="right">
              <p>
                {setAsideCode.find((n) => n.keys[0] === SetASideCode)
                  ?.label ?? (
                  <span className="text-muted-foreground">
                    No description available{" "}
                  </span>
                )}
              </p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ),
      label: "Set Aside",
      icon: AiOutlineFileProtect,
    },
  ];
  return (
    <BaseCard
      id={id}
      title={Title}
      summary={Summary}
      tags={tags}
      dueDate={formatDate(metadata[searchTypes.federal.due])}
      postedDate={formatDate(metadata[searchTypes.federal.posted])}
      tagColCount={3}
      {...props}
    />
  );
}

function SledCard({ rowData, ...props }) {
  const { id, metadata } = rowData;
  const {
    title,
    summary,
    type,
    dueDate,
    agency,
    state,
    startDate,
    jurisdiction,
    level,
  } = metadata;

  const tags = [
    { value: toPascalCase(agency), label: "Agency", icon: Landmark },
    { value: toPascalCase(type), label: "Type", icon: LiaFileContractSolid },
    { value: toPascalCase(state), label: "State", icon: IoLocationOutline },
    {
      value: toPascalCase(jurisdiction),
      label: "Jurisdiction",
      icon: IoLocationOutline,
    },
    {
      value: toPascalCase(level),
      label: "Level",
      icon: IoLocationOutline,
    },
  ];
  return (
    <BaseCard
      id={id}
      title={title}
      summary={summary}
      tags={tags}
      dueDate={formatDate(metadata[searchTypes.sled.due])}
      postedDate={formatDate(metadata[searchTypes.sled.posted])}
      tagColCount={3}
      {...props}
    />
  );
}

function GrantCard({ rowData, ...props }) {
  const { id, metadata } = rowData;
  const {
    opportunityTitle,
    summary,
    agencyDetails,
    oppStatus,
    responseDate,
    opportunityCategory,
    postingDate,
  } = metadata;

  const tags = [
    { value: agencyDetails, label: "Agency" },
    { value: toPascalCase(opportunityCategory), label: "Category" },
    { value: toPascalCase(oppStatus), label: "Type" },
  ];

  return (
    <BaseCard
      id={id}
      title={opportunityTitle}
      summary={summary}
      tags={tags}
      dueDate={formatDate(metadata[searchTypes.grant.due])}
      postedDate={formatDate(metadata[searchTypes.grant.posted])}
      {...props}
    />
  );
}

function SbirCard({ rowData, ...props }) {
  const { id, metadata } = rowData;
  const {
    title,
    summary,
    agency,
    closeDate,
    phase,
    program,
    branch,
    releaseDate,
  } = metadata;
  const tags = [
    { value: agency, label: "Agency" },
    { value: toPascalCase(phase), label: "Phase" },
    { value: program, label: "Program" },
    { value: branch, label: "Branch" },
  ];
  return (
    <BaseCard
      id={id}
      title={title}
      summary={summary}
      tags={tags}
      dueDate={formatDate(metadata[searchTypes.sbir.due])}
      postedDate={formatDate(metadata[searchTypes.sbir.posted])}
      {...props}
    />
  );
}

function SearchCard({ rowData, ...props }) {
  const { isLoading, searchType } = useContext(SearchContext);

  if (!isLoading) {
    switch (searchType) {
      case "federal":
        return <FederalCard rowData={rowData} {...props} />;
      case "sled":
        return <SledCard rowData={rowData} {...props} />;
      case "grant":
        return <GrantCard rowData={rowData} {...props} />;
      case "sbir":
        return <SbirCard rowData={rowData} {...props} />;
      default:
        return null;
    }
  } else {
    return (
      <Card role="row">
        <ThemedSkeleton className="w-12/12" />
        <div>
          <ThemedSkeleton className="w-11/12" />
          <ThemedSkeleton className="w-10/12" />
          <ThemedSkeleton className="w-11/12" />
          <ThemedSkeleton className="w-8/12" />
          <ThemedSkeleton className="w-9/12" />
        </div>
        <ThemedSkeleton className=" w-40" />
        <div className="flex gap-4">
          <ThemedSkeleton className="w-36 rounded-full" />
          <ThemedSkeleton className="w-36 rounded-full" />
          <ThemedSkeleton className="w-36 rounded-full" />
        </div>
      </Card>
    );
  }
}

export default SearchCard;
